import { FadeIn } from "@/components/Reveal";
import ChapterHeader from "@/components/ChapterHeader";
import { ArrowUpRight } from "lucide-react";
import sanjivPortrait from "@/assets/sanjiv-lall.jpg";

// External ventures / websites — rendered as clickable chips at the end.
const VENTURES = [
  { label: "Kleinstar", href: "https://kleinstar.co.nz", note: "SAP consulting (NZ)" },
  { label: "XO Goa", href: "https://XOgoa.com", note: "Heritage villas · Goa" },
  { label: "India Jeeps", href: "https://indiajeeps.com", note: "WWII & CJ3B jeeps" },
  { label: "Rubies in my Ears", href: "https://rubiesinmyears.com", note: "Memoir & audiobook" },
  { label: "@the_rough_trails", href: "https://www.instagram.com/the_rough_trails", note: "Motivational videos" },
  { label: "Tikratoli", href: "https://tikratoli.com", note: "This farm resort" },
];

function Link({ href, children, testid }) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      data-testid={testid}
      className="text-[#D97645] hover:text-[#F2E7D0] underline underline-offset-4 decoration-[#D97645]/40 hover:decoration-[#F2E7D0] transition"
    >
      {children}
    </a>
  );
}

export default function Roots() {
  return (
    <main data-testid="roots-page" className="pt-32 pb-24">
      {/* Hero */}
      <section className="mx-auto max-w-[1600px] px-6 lg:px-12 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">
        <FadeIn className="lg:col-span-7">
          <div className="stamp mb-8">Vol · XII · Roots</div>
          <h1 className="font-display text-6xl md:text-8xl leading-[0.95] text-[#F2E7D0] max-w-5xl">
            The man behind <em className="italic text-[#D97645]">the estate</em>.
          </h1>
          <p className="mt-8 max-w-2xl text-[#B8A891] text-lg leading-relaxed">
            Sanjiv T Lall — motivational &amp; leadership speaker. 49 years of cross-border
            entrepreneurship, from pioneering SAP consulting across continents to planting an orchard
            in Ranchi and restoring 1942 Willys jeeps.
          </p>
        </FadeIn>
        <FadeIn delay={0.15} className="lg:col-span-5">
          <figure data-testid="roots-portrait" className="relative max-w-md lg:max-w-none mx-auto">
            <div className="aspect-[4/5] overflow-hidden border border-[#F2E7D0]/10 bg-[#27221C]">
              <img
                src={sanjivPortrait}
                alt="Sanjiv T Lall seated on a vintage blue Willys jeep in the Tikratoli sal forest"
                className="w-full h-full object-cover"
                loading="eager"
              />
            </div>
            <figcaption data-testid="roots-portrait-caption" className="mt-3 font-mono-label text-[#B8A891]">
              Sanjiv T Lall · Sal Forest
            </figcaption>
          </figure>
        </FadeIn>
      </section>

      {/* Chapter 01 — Beginning */}
      <section className="mx-auto max-w-[1600px] px-6 lg:px-12 py-20 grid grid-cols-1 lg:grid-cols-12 gap-10">
        <FadeIn className="lg:col-span-5">
          <ChapterHeader num="01" kicker="1977 — 1978" title="Tehran. And a 72-hour exit." />
        </FadeIn>
        <FadeIn delay={0.1} className="lg:col-span-7">
          <p className="text-[#B8A891] leading-relaxed text-lg">
            Sanjiv began his working career in <span className="text-[#F2E7D0]">Tehran, Iran</span>, in July 1977 with
            <span className="text-[#F2E7D0]"> SeaLand Inc</span> — the world's largest containerised shipping company — straight after
            completing his B.A. from St. Xavier's College, Calcutta.
          </p>
          <p className="mt-5 text-[#B8A891] leading-relaxed text-lg">
            In late 1978, Sanjiv was given <span className="text-[#F2E7D0]">72 hours to exit Iran</span> by the then
            autocrat Shah of Iran's secret service, <em className="italic">"SAVAK"</em>. He escaped via Moscow,
            Scandinavia, UK and Europe to <span className="text-[#F2E7D0]">Madrid, Spain</span>, where he began his second stint
            with SeaLand.
          </p>
        </FadeIn>
      </section>

      {/* Chapter 02 — Kleinstar */}
      <section className="bg-[#27221C] py-20">
        <div className="mx-auto max-w-[1600px] px-6 lg:px-12 grid grid-cols-1 lg:grid-cols-12 gap-10">
          <FadeIn className="lg:col-span-5">
            <ChapterHeader num="02" kicker="1994" title="Pioneering SAP across continents." />
          </FadeIn>
          <FadeIn delay={0.1} className="lg:col-span-7">
            <p className="text-[#B8A891] leading-relaxed text-lg">
              In 1994, Sanjiv founded <Link href="https://kleinstar.co.nz" testid="link-kleinstar">Kleinstar Holdings Limited</Link>, New Zealand —
              pioneering SAP consulting in New Zealand <em className="italic">and</em> India. He was the first person to bring expat
              SAP consultants to both countries.
            </p>
            <p className="mt-5 text-[#B8A891] leading-relaxed text-lg">
              Over the years, he built and led international SAP consulting companies spanning the USA, India, Australasia,
              the Middle East and beyond — securing landmark contracts and mentoring hundreds of SAP consultants to their fullest potential.
              Prestigious Indian clients included <span className="text-[#F2E7D0]">Reliance Industries</span> and <span className="text-[#F2E7D0]">TELCO</span> (now Tata Motors), Mumbai.
            </p>
          </FadeIn>
        </div>
      </section>

      {/* Chapter 03 — Travel + Goa + Jeeps */}
      <section className="mx-auto max-w-[1600px] px-6 lg:px-12 py-20 grid grid-cols-1 lg:grid-cols-12 gap-10">
        <FadeIn className="lg:col-span-5">
          <ChapterHeader num="03" kicker="2003 onwards" title="Goa. Jeeps." />
        </FadeIn>
        <FadeIn delay={0.1} className="lg:col-span-7 space-y-5">
          <p className="text-[#B8A891] leading-relaxed text-lg">
            From launching and scaling SAP consulting across the globe, to renovating heritage villas in Goa — <Link href="https://XOgoa.com" testid="link-xogoa">XO Goa</Link> —
            Sanjiv has consistently turned uncertainty into opportunity, monetising challenges into lasting impact.
          </p>
          <p className="text-[#B8A891] leading-relaxed text-lg">
            Today he has the <span className="text-[#F2E7D0]">largest collection of vintage WWII and CJ3B jeeps in Jharkhand</span> — <Link href="https://indiajeeps.com" testid="link-indiajeeps">India Jeeps</Link>.
          </p>
        </FadeIn>
      </section>

      {/* Chapter 04 — Author + Speaker */}
      <section className="bg-[#27221C] py-20">
        <div className="mx-auto max-w-[1600px] px-6 lg:px-12 grid grid-cols-1 lg:grid-cols-12 gap-10">
          <FadeIn className="lg:col-span-5">
            <ChapterHeader num="04" kicker="2018" title={<>"Rubies in My Ears."</>} />
          </FadeIn>
          <FadeIn delay={0.1} className="lg:col-span-7 space-y-5">
            <p className="text-[#B8A891] leading-relaxed text-lg">
              Sanjiv authored <Link href="https://rubiesinmyears.com" testid="link-rubies">Rubies in My Ears</Link> in 2018 — a personal memoir documenting adversity,
              perseverance, and the mindset required to convert setbacks into triumphs. He has also narrated the memoir in his own voice as an audiobook.
            </p>
            <p className="text-[#B8A891] leading-relaxed text-lg">
              Sanjiv is a motivational speaker drawing from 49 years of hands-on business experience. He has given talks at educational institutions across
              India, the UK and New Zealand — including his own alma mater, <span className="text-[#F2E7D0]">The Lawrence School Sanawar</span>.
              His motivational videos live at <Link href="https://www.instagram.com/the_rough_trails" testid="link-rough-trails">@the_rough_trails</Link>.
            </p>
          </FadeIn>
        </div>
      </section>

      {/* Chapter 05 — Tikratoli */}
      <section className="mx-auto max-w-[1600px] px-6 lg:px-12 py-20 grid grid-cols-1 lg:grid-cols-12 gap-10">
        <FadeIn className="lg:col-span-5">
          <ChapterHeader num="05" kicker="2020" title="Tikratoli Farm Resorts." />
        </FadeIn>
        <FadeIn delay={0.1} className="lg:col-span-7 space-y-5">
          <p className="text-[#B8A891] leading-relaxed text-lg">
            In 2020, Sanjiv launched <Link href="https://tikratoli.com" testid="link-tikratoli">Tikratoli Farm Resorts</Link> — a unique resort showcasing the
            <span className="text-[#F2E7D0]"> first full steel building in Jharkhand</span>, built along the lines of a true Buddhist monastery and evoking a total sense
            of <em className="italic">calmness &amp; serenity</em>.
          </p>
          <p className="text-[#B8A891] leading-relaxed text-lg">
            To complement the restaurant <span className="text-[#F2E7D0]">Sunshine Kitchen</span> and its live counter — coined <em className="italic">"Khujli Live"</em> —
            a customised shipping container was fashioned as an AC bar &amp; restaurant, again a first of its kind in Jharkhand: <span className="text-[#F2E7D0]">Bablu Da Theka</span>.
          </p>
        </FadeIn>
      </section>

      {/* Philosophy */}
      <section className="bg-[#1C1913] border-t border-[#3A3229] py-24">
        <div className="mx-auto max-w-[1600px] px-6 lg:px-12">
          <ChapterHeader num="06" kicker="Philosophy" title={<>Sanjiv's philosophy — <em className="italic">at a glance</em>.</>} />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
            <FadeIn>
              <p className="text-[#F2E7D0] leading-relaxed font-display text-xl italic">
                "Success is earned through thought, desire and unwavering commitment — which is the primary reason most people fail."
              </p>
            </FadeIn>
            <FadeIn delay={0.1}>
              <p className="text-[#B8A891] leading-relaxed">
                Education degrees open doors — but consistency, effort, resilience and attitude sustain progress.
                Leadership means elevating others, shaping opportunities, and acting with integrity, curiosity and courage.
                Growth comes from embracing uncertainty, taking calculated risks, and learning from past experiences.
                Mistakes are part of growth. Persistence leads to achievement. To succeed, you must — <em className="italic text-[#F2E7D0]">take charge of your life</em>.
              </p>
            </FadeIn>
          </div>
        </div>
      </section>

      {/* Working with Sanjiv */}
      <section className="mx-auto max-w-[1600px] px-6 lg:px-12 py-20">
        <ChapterHeader num="07" kicker="Working with Sanjiv" title="What you can expect." />
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {[
            "Forty-nine years of practical, international leadership and entrepreneurial experience.",
            "Actionable, real-world guidance that translates into tangible outcomes.",
            "A collaborative, growth-oriented partnership focused on resilience, innovation and ethical leadership.",
            "A powerful blend of motivation, faith-inspired leadership, and proven results across diverse sectors.",
          ].map((t) => (
            <FadeIn key={t}>
              <div className="border border-white/10 p-6 h-full text-[#F2E7D0] leading-relaxed">{t}</div>
            </FadeIn>
          ))}
        </div>
      </section>

      {/* Ventures / Links wall */}
      <section className="bg-[#27221C] py-20">
        <div className="mx-auto max-w-[1600px] px-6 lg:px-12">
          <ChapterHeader num="08" kicker="Ventures" title="The websites &amp; collections." />
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {VENTURES.map((v) => (
              <FadeIn key={v.href}>
                <a
                  href={v.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={`venture-${v.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                  className="group block border border-white/10 hover:border-[#D97645] p-6 transition"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <div className="font-display text-2xl text-[#F2E7D0] group-hover:text-[#D97645] transition">{v.label}</div>
                      <div className="mt-2 font-mono-label text-xs text-[#B8A891]">{v.note}</div>
                      <div className="mt-3 font-mono-label text-[10px] tracking-[0.2em] text-[#D97645] break-all">
                        {v.href.replace(/^https?:\/\//, "")}
                      </div>
                    </div>
                    <ArrowUpRight size={18} className="text-[#B8A891] group-hover:text-[#D97645] transition shrink-0 mt-1" />
                  </div>
                </a>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>
    </main>
  );
}
